import { ICrewInput } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import _ from "lodash";
import { createRef, PureComponent, ReactNode } from "react";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./ai-agent-modals.scss";

const TYPE_OPTIONS = [
  { value: "str", label: "string" },
  { value: "bool", label: "boolean" },
  { value: "float", label: "float" },
  { value: "integer", label: "integer" }
];

export interface IAiAgentInputsModalProps {
  isOpen: boolean;
  inputs: ICrewInput[];
  onClose: () => void;
  onAgentInputsUpdate: (inputs: ICrewInput[]) => void;
}

export interface IAiAgentInputsModalState {
  isLoading?: boolean;
  /**
   * Where Update stands. It writes into the agent being edited, not to the server, so there is no
   * loading phase — only the ✓ held long enough to be read before the dialog leaves.
   */
  applyStatus?: KuikaButtonStatus;
}

export class AiAgentInputsModal extends PureComponent<IAiAgentInputsModalProps, IAiAgentInputsModalState> {
  formRef = createRef<FormInstance<any>>();
  addFieldFn: ((defaultValue?: any, insertIndex?: number) => void) | null = null;

  private readonly applyStatus: SaveStatus = new SaveStatus((applyStatus: KuikaButtonStatus): void => {
    this.setState({ applyStatus });
  });

  componentWillUnmount(): void {
    this.applyStatus.dispose();
  }

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidUpdate(
    prevProps: Readonly<IAiAgentInputsModalProps>,
    prevState: Readonly<IAiAgentInputsModalState>,
    snapshot?: any
  ): void {
    if (prevProps.isOpen === false && this.props.isOpen === true) {
      this.handleLoad();
    }
  }

  handleLoad = () => {
    this.formRef.current?.setFieldsValue({
      inputs: _.cloneDeep(this.props.inputs)
    });
  };

  onSubmit = (data: { inputs: ICrewInput[] }) => {
    // A second press during the ✓ would write the same inputs twice.
    if (!this.applyStatus.canStart) return;
    // The agent takes the inputs at once; the ✓ is held on Update, then the dialog leaves.
    this.props.onAgentInputsUpdate(data.inputs);
    this.applyStatus.succeed(this.props.onClose);
  };

  closeModal = (): void => {
    if (this.state.isLoading) return;
    this.props.onClose();
  };

  parameterNameValidator = async (index: number, value?: string) => {
    if (!value) throw new Error("This field is required");
    if (!/^[A-Za-z_-][A-Za-z0-9_-]*$/.test(value)) throw new Error("Please enter a valid parameter key.");

    const { inputs } = this.formRef.current?.getFieldsValue();

    if (inputs.find((param, i) => param.key === value && i !== index)) {
      throw new Error("This key value is used before.");
    }
  };

  render(): ReactNode {
    return (
      <>
        <Form ref={this.formRef} onFinish={this.onSubmit}>
          <KModal
            className="kuika-ai-agent-response-modal"
            header="AI Agent Inputs"
            showModal={this.props.isOpen}
            closeModal={this.closeModal}
            width={720}
            isLoading={this.state.isLoading}
            footer={
              <KModalFooter
                submitLabel="Update"
                status={this.state.applyStatus ?? "idle"}
                statusLabels={{ success: "Updated" }}
                onSubmit={() => {
                  this.formRef.current?.submit();
                }}
              >
                <KuikaButton
                  htmlType="button"
                  type="tertiary"
                  color="neutral"
                  size="md"
                  uppercase
                  onClick={() => {
                    this.addFieldFn?.({
                      parameter_name: "",
                      parameter_format: "str",
                      description: ""
                    });
                  }}
                >
                  Add field
                </KuikaButton>
              </KModalFooter>
            }
          >
            <div className="kuika-ai-agent-response-modal__container kuika-ai-agent-response-modal__container--auto">
              <Form.List name="inputs">
                {(fields, { add, remove }) => {
                  this.addFieldFn = add;
                  return (
                    <>
                      {fields.length === 0 && (
                        <KuikaEmptyState
                          svgName="field"
                          svgType="common"
                          name="field"
                          pastTense="added"
                          // description="to get started."
                        />
                      )}
                      {fields.length > 0 &&
                        fields.map(({ name, fieldKey, ...restField }) => (
                          <div className="kuika-ai-agent-response-modal__item" key={name}>
                            <div className="kuika-ai-agent-response-modal__field">
                              <Form.Item
                                name={[name, "parameter_name"]}
                                rules={[
                                  {
                                    validator: (_rule, value) => this.parameterNameValidator(name, value)
                                  }
                                ]}
                                {...restField}
                              >
                                <KuikaInput
                                  placeholder="Field name"
                                  onKeyDown={(e) => {
                                    if (!e.key.match(/^[a-zA-Z0-9_]*$/)) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </Form.Item>
                            </div>

                            <div className="kuika-ai-agent-response-modal__field">
                              <Form.Item
                                name={[name, "parameter_format"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "This field is required"
                                  }
                                ]}
                                {...restField}
                              >
                                <KuikaSelectbox options={TYPE_OPTIONS} placeholder="Type" />
                              </Form.Item>
                            </div>

                            <div className="kuika-ai-agent-response-modal__actions">
                              <KuikaButton type="tertiary" color="neutral" isIcon onClick={() => remove(name)}>
                                <KuikaSvg type="common" name="trash" color="var(--red-5)" />
                              </KuikaButton>
                            </div>
                          </div>
                        ))}
                    </>
                  );
                }}
              </Form.List>
            </div>
          </KModal>
        </Form>
      </>
    );
  }
}
